<script setup>
import { ref } from 'vue'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import * as z from 'zod'
import { API, HOST } from '@/utils/http'

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { PlusCircle } from 'lucide-vue-next'

const emit = defineEmits(['organization-created'])

const isOpen = ref(false)
const close = () => {
  isOpen.value = false
}

const formSchema = toTypedSchema(z.object({
  name: z.string().min(3).max(255)
}))

const onSubmit = async (values) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/admin/organizations`,
      data: {
        organization_name: values.name
      }
    })

    toast({
      title: `Organization ${values.name} created`,
    })

    emit('organization-created', response.data.organization_id)
    close()
  } catch (error) {
    toast({
      title: 'Error',
      description: error.response?.data?.detail || error.message,
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger>
      <slot>
        <Button class="px-2" variant="ghost">
          <PlusCircle class="text-muted-foreground w-4 h-4" />
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle>Create organization</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      <Form class="space-y-3" :validation-schema="formSchema" @submit="onSubmit">
        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <DialogFooter>
          <Button variant="ghost" @click="close">Close</Button>
          <Button type="submit">Create</Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>
</template>
